// src/components/EditableName.jsx
// A name (rendered as the given heading tag) with a pencil that turns it into an input.
// Enter or leaving the field saves, Escape cancels. `onSave(name)` resolves to true when it worked;
// if it doesn't, the field stays open so the person can fix the name or press Escape.
import React, { useEffect, useRef, useState } from 'react';
import { PencilIcon } from './icons';

export const MAX_NAME_LENGTH = 60; // keep in sync with Tournament.MAX_NAME_LENGTH in backend/entities.py

const tidy = (text) => text.split(/\s+/).filter(Boolean).join(' ');

function EditableName({ name, onSave, as: Tag = 'span', inputLabel = 'Name', className = '' }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(name);
    const inputRef = useRef(null);
    const cancelled = useRef(false);
    const saving = useRef(false);

    useEffect(() => {
        if (editing) {
            inputRef.current?.focus();
            inputRef.current?.select();
        }
    }, [editing]);

    const start = () => {
        setDraft(name);
        cancelled.current = false;
        setEditing(true);
    };

    const cancel = () => {
        cancelled.current = true;
        setEditing(false);
    };

    const commit = async () => {
        if (cancelled.current || saving.current) return;
        const next = tidy(draft);
        if (!next || next === name) {
            setEditing(false); // nothing to save
            return;
        }
        saving.current = true;
        const saved = await onSave(next);
        saving.current = false;
        if (saved) {
            setEditing(false);
        } else {
            inputRef.current?.focus();
        }
    };

    const onKeyDown = (event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commit();
        } else if (event.key === 'Escape') {
            cancel();
        }
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                className={`input name-input ${className}`}
                aria-label={inputLabel}
                value={draft}
                maxLength={MAX_NAME_LENGTH}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={onKeyDown}
                onBlur={commit}
            />
        );
    }

    return (
        <span className="editable-name">
            <Tag className={className}>{name}</Tag>
            <button className="btn btn-ghost btn-icon btn-sm" aria-label={`Rename ${name}`} onClick={start}>
                <PencilIcon size={15} />
            </button>
        </span>
    );
}

export default EditableName;
